import { t } from '@lingui/core/macro';
import { Plural, Trans } from '@lingui/react/macro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { AlertCircle } from '@untitledui/icons/AlertCircle';
import { HelpCircle } from '@untitledui/icons/HelpCircle';
import { includes } from 'lodash-es';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { KindOfResearch } from '@/api/core-api/types.gen';

import {
  PrequalificationResult,
  ProjectDisqualificationReason,
  ScreenerResponseStatus,
} from '@/api/core-api/types.gen';
import { Button } from '@/components/base/buttons/button';
import { Card } from '@/components/base/card/card';
import { notify } from '@/components/base/notifications/notifications';
import { Tooltip, TooltipTrigger } from '@/components/base/tooltip/tooltip';
import { ProjectStatusBadge } from '@/components/foundations/project-status-badge';
import { ProjectSpecificReferralsCard } from '@/components/referral-cards/project-specific-referral-card';
import SpecialInstructionCard from '@/components/widgets/special-instruction-card';
import TwoStepsToQualifyCard from '@/components/widgets/two-steps-to-qualify-card';
import { BASE_PATH } from '@/constants/config';
import { queryKeys } from '@/hooks/api/query-keys';
import { useHideProject } from '@/hooks/api/use-hide-project';
import { useProfile } from '@/hooks/api/use-profile';
import { useCaptureParticipantUtmFromSearch } from '@/hooks/use-capture-participant-utm-from-search';
import { useLinkedinVolOAuthReturn } from '@/hooks/use-linkedin-vol-oauth-return';
import { usePersistProjectReferralFromSearch } from '@/hooks/use-persist-project-referral-from-search';
import { usePersistProjectUtmFromSearch } from '@/hooks/use-persist-project-utm-from-search';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { DEFAULT_PROFILE_PICTURE_URL } from '@/utils/constants';
import { clearLinkedinVolReturnPathCookie } from '@/utils/linkedin-vol-return-cookie';
import {
  getParticipantUtmSearchForRouter,
  hasParticipantReferralAdvertUtm,
} from '@/utils/participant-utm-storage';
import { consumeProjectTaskCompleteLocally } from '@/utils/project-task-complete-storage';

import type { ProjectSearchParams } from '../-shared';

import { projectQueryOptions } from '../-shared';
import { NotInterestedButton } from '../../-components/not-interested-button';
import { ResearchMethodBadge } from '../../-components/research-method-badge';
import { getTaskProgress } from '../../../-utils/task-progress';
import { ApplyButton } from './apply-button';
import { BackButton } from './back-button';
import { CancelApplicationPrompt } from './cancel-application-prompt';
import { FaqSection } from './faq-section';
import { PlaidVerification } from './plaid-verification';
import { PreApplyModal } from './pre-apply-modal';
import { ProjectViewError } from './project-access-error';
import { ProjectMetadataRow } from './project-metadata-row';
import {
  ProjectStatusProgressSteps,
  isStatusShowingProgress,
} from './project-status-progress-steps';
import { ReferralBanner } from './referral-banner';
import { ScreenerLimitDisplay } from './screener-limit-banner';
import { StatusBanner } from './status-banner';
import { WhatToExpect } from './what-to-expect';
import { WorkEmailVerificationModal } from './work-email-verification-modal';

const rootRoute = getRouteApi('__root__');

interface ProjectDetailPageProps {
  projectId: string;
  search?: ProjectSearchParams;
}

export function ProjectDetail({ projectId, search }: ProjectDetailPageProps) {
  const { user } = rootRoute.useRouteContext();
  const isAuthenticated = !!user;

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useLinkedinVolOAuthReturn(user?.id);
  usePersistProjectReferralFromSearch(projectId, search);
  useCaptureParticipantUtmFromSearch(search);
  usePersistProjectUtmFromSearch(projectId, search);
  useLayoutEffect(() => {
    if (consumeProjectTaskCompleteLocally(projectId)) {
      setLocalTaskComplete(true);
    }
  }, [projectId]);

  const hideProject = useHideProject();
  const [showPreApplyModal, setShowPreApplyModal] = useState(false);

  const [showPlaidVerificationModal, setShowPlaidVerificationModal] =
    useState(false);

  const [showWorkEmailVerificationModal, setShowWorkEmailVerificationModal] =
    useState(false);
  // Consume after mount (sessionStorage is unavailable during SSR).
  const [localTaskComplete, setLocalTaskComplete] = useState(false);

  const analytics = useAnalytics();
  const { data: profileData, refetch: refetchProfile } = useProfile(user?.id);

  const openWorkEmailVerification = useCallback(() => {
    void (async () => {
      await refetchProfile();
      setShowWorkEmailVerificationModal(true);
    })();
  }, [refetchProfile]);

  const { data: result, isError } = useQuery(
    projectQueryOptions(projectId, search, user?.id),
  );

  // project_viewed: User sees the project detail page
  const hasFiredProjectView = useRef(false);
  useEffect(() => {
    const projectData = result?.success && result.response;

    if (
      !projectData ||
      !analytics ||
      hasFiredProjectView.current ||
      !projectData.project
    ) {
      return;
    }
    hasFiredProjectView.current = true;

    analytics.screen({
      name: 'projectDetail',
      attributes: {
        projectId,
        isEligible:
          !projectData.project.prequalifies ||
          projectData.project.prequalifies.result !==
            PrequalificationResult.DISQUALIFIED,
        source: search?.inviteId ? 'invited' : 'dashboard',
      },
    });

    // project_not_eligible_shown
    if (
      projectData.project.prequalifies?.result ===
      PrequalificationResult.DISQUALIFIED
    ) {
      analytics.track({
        action: 'shown',
        actionSubject: 'projectNotEligible',
        attributes: {
          projectId,
          ineligibilityReason:
            projectData.project.prequalifies.disqualificationReason ?? null,
        },
      });
    }
  }, [analytics, projectId, result, search]);

  const hasAlreadyApplied = useMemo(() => {
    if (!result?.success || !result.response) return false;

    const { screenerResponseStatus } = result.response;

    return (
      !!screenerResponseStatus &&
      !includes(
        [
          ScreenerResponseStatus.DRAFT,
          ScreenerResponseStatus.EXTERNAL_SCREENER_IN_PROGRESS,
        ],
        screenerResponseStatus,
      )
    );
  }, [result]);

  const handleNotInterested = () => {
    if (!result?.success || !result.response?.id) return;

    hideProject.mutate(result.response.id, {
      onSuccess: () => {
        notify({
          title: t`Project hidden`,
          description: t`We'll hide this project from your matches.`,
          color: 'success',
        });
        void navigate({ to: '/participants/projects' });
      },
    });
  };

  const requiredPreApplicationsSteps = useMemo(() => {
    const steps: Array<'picture'> = [];

    if (!profileData || !result?.response) return steps;

    if (
      !profileData.profile.picture ||
      profileData.profile.picture === DEFAULT_PROFILE_PICTURE_URL
    ) {
      steps.push('picture');
    }
    return steps;
  }, [profileData, result?.response]);

  // A B2B project the participant fails only on the work-email gate. Apply
  // stays enabled and opens the same verification modal as the banner, so
  // the participant is not left with a dead "Not eligible" button.
  const needsWorkEmailVerification = useMemo(() => {
    if (!isAuthenticated || !result?.success) return false;

    const prequalifies = result.response?.project?.prequalifies;

    return (
      prequalifies?.result === PrequalificationResult.DISQUALIFIED &&
      (prequalifies.disqualificationReason ===
        ProjectDisqualificationReason.WORK_EMAIL_REQUIRED ||
        prequalifies.disqualificationReason ===
          ProjectDisqualificationReason.WORK_EMAIL_NEEDS_REVERIFICATION)
    );
  }, [isAuthenticated, result]);

  const handleApply = useCallback(() => {
    void navigate({
      to: '/participants/projects/$projectId/apply',
      params: { projectId },
      search: {
        invite: search?.invite,
        inviteCode: search?.inviteCode,
        inviteId: search?.inviteId,
      },
    });
  }, [navigate, projectId, search]);

  const showSignupToApply = hasParticipantReferralAdvertUtm(search);

  const handleApplyClick = useCallback(() => {
    if (!isAuthenticated) {
      const authRoute = showSignupToApply
        ? '/participants/signup'
        : '/participants/login';

      void navigate({
        to: authRoute,
        search: {
          returnTo: location.pathname + location.search,
          ...getParticipantUtmSearchForRouter(search),
        },
      });
      return;
    }

    if (needsWorkEmailVerification) {
      openWorkEmailVerification();
      return;
    }

    if (requiredPreApplicationsSteps.length > 0) {
      setShowPreApplyModal(true);
      return;
    }

    handleApply();
  }, [
    handleApply,
    isAuthenticated,
    navigate,
    needsWorkEmailVerification,
    openWorkEmailVerification,
    requiredPreApplicationsSteps.length,
    search,
    showSignupToApply,
  ]);

  if (isError) {
    return (
      <div className="mx-auto max-w-[var(--max-width-container)] px-4 pt-6 pb-18 md:px-8">
        <div className="flex flex-col items-center py-16 text-center">
          <AlertCircle className="mb-4 size-12 text-fg-quaternary" />
          <h1 className="mb-2 font-heading text-xl font-semibold text-primary">
            <Trans>Project not found</Trans>
          </h1>
          <p className="mb-6 text-secondary">
            <Trans>
              This project may have been removed or is no longer available.
            </Trans>
          </p>
          <Button
            color="primary"
            size="lg"
            onClick={() => navigate({ to: '/participants/projects' })}
          >
            <Trans>Browse projects</Trans>
          </Button>
        </div>
      </div>
    );
  }

  if (!result?.success) {
    return <ProjectViewError projectId={projectId} errors={result?.errors} />;
  }

  const project = result?.response?.project;
  const projectData = result.response;

  if (!project || !projectData) {
    return <ProjectViewError projectId={projectId} />;
  }

  const isTaskComplete =
    Boolean(projectData.isTaskComplete) || localTaskComplete;
  const taskProgress = getTaskProgress({
    status: projectData.screenerResponseStatus,
    projectStarted: projectData.projectStarted,
    projectStartedAt: projectData.projectStartedAt,
    isTaskComplete,
    taskCompletedAt: projectData.taskCompletedAt,
  });

  const isProjectClosed = projectData.isProjectClosed;
  const isClosedToNewApplications = projectData.isClosedToNewApplications;
  const isStudyOver = projectData.isStudyOver;
  const isTargetReached = projectData.isTargetReached;

  // Check if user can apply (qualifies, prequalification not yet checked, or
  // only the work-email gate stands in the way — Apply then verifies first)
  const canApplyPrequalification =
    !project.prequalifies ||
    project.prequalifies.result !== PrequalificationResult.DISQUALIFIED ||
    needsWorkEmailVerification;

  // Check if user has reached screener limit
  const screenerRemaining = projectData.screenerCycle
    ? Math.max(
        projectData.screenerCycle.limit -
          projectData.screenerCycle.screenerCount,
        0,
      )
    : undefined;

  // Falls back to `isMatched` for an API that predates the field, since web
  // and core-api deploy independently.
  const isExemptFromScreenerLimit =
    projectData.isExemptFromScreenerLimit ?? projectData.isMatched;

  const isAtScreenerLimit =
    screenerRemaining === 0 && !isExemptFromScreenerLimit;

  const canApply = canApplyPrequalification && !isAtScreenerLimit;

  if (showPlaidVerificationModal && user) {
    return (
      <PlaidVerification
        userId={user.id}
        projectId={projectId}
        onCancel={() => {
          setShowPlaidVerificationModal(false);
        }}
        onSuccess={async () => {
          setShowPlaidVerificationModal(false);
          await refetchProfile();
          await queryClient.refetchQueries({
            queryKey: queryKeys.projects.detailBase(projectId),
          });
          handleApplyClick();
        }}
      />
    );
  }

  return (
    <>
      <PreApplyModal
        isOpen={showPreApplyModal}
        onSuccess={async () => {
          setShowPreApplyModal(false);
          await refetchProfile();
          await queryClient.refetchQueries({
            queryKey: queryKeys.projects.detailBase(projectId),
          });
          handleApply();
        }}
        onCancel={() => {
          setShowPreApplyModal(false);
          clearLinkedinVolReturnPathCookie();
          refetchProfile();
        }}
        initialPhone={profileData?.profile.phone ?? ''}
        profileId={profileData?.profile.id}
        userId={user?.id}
        steps={requiredPreApplicationsSteps}
      />
      <div className="mx-auto max-w-(--max-width-container) px-4 pt-6 pb-18 md:px-8">
        {/* Header Row: Back Navigation + Screener Limit Display */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <BackButton projectId={projectId} />

          {/* Screener Limit Display - only for authenticated users who have not yet applied */}
          {isAuthenticated &&
            projectData.screenerCycle &&
            !isExemptFromScreenerLimit &&
            !hasAlreadyApplied && (
              <ScreenerLimitDisplay data={projectData.screenerCycle} />
            )}

          {isAuthenticated &&
            projectData.screenerResponseStatus &&
            isStatusShowingProgress(projectData.screenerResponseStatus) && (
              <ProjectStatusProgressSteps
                kindOfResearch={project.kindOfResearch}
                status={projectData.screenerResponseStatus}
              />
            )}
        </div>

        {/* Project Status Banner */}
        <StatusBanner
          taskProgress={taskProgress}
          projectData={projectData}
          isProjectClosed={isProjectClosed}
          isClosedToNewApplications={isClosedToNewApplications}
          isStudyOver={isStudyOver}
          isTargetReached={isTargetReached}
          exposeParticipantEmails={
            projectData.project?.exposeParticipantEmails ?? false
          }
          hasAlreadyApplied={hasAlreadyApplied}
          projectId={projectId}
          projectName={project.name ?? ''}
          user={user}
          kindOfResearch={project.kindOfResearch}
          prequalifies={project.prequalifies}
          requiresScreenShare={project.screenRecordingEnabled ?? false}
          onVerifyWorkEmail={openWorkEmailVerification}
        />

        <div className="max-w-304 space-y-6">
          {/* Two Steps to Qualify — when double-screening-alert flag is enabled for the org */}
          {projectData.showDoubleScreeningAlert && <TwoStepsToQualifyCard />}

          {/* Instruction Card — only on invite screen when instructions exist */}
          {projectData.screenerResponseStatus ===
            ScreenerResponseStatus.INVITED &&
            project.specialInstructions && (
              <SpecialInstructionCard
                specialInstructions={project.specialInstructions}
              />
            )}

          {/* Main Project Card */}
          <Card className="rounded-2xl p-6 shadow-xs md:p-8">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <ResearchMethodBadge
                  kindOfResearch={project.kindOfResearch as KindOfResearch}
                  researchConductType={project.researchConductType}
                />
                <ProjectStatusBadge
                  status={projectData.screenerResponseStatus}
                  payout={{ payoutStatus: projectData.payoutStatus }}
                  isProjectClosed={isProjectClosed}
                  isClosedToNewApplications={isClosedToNewApplications}
                  isTaskComplete={isTaskComplete}
                />
              </div>
              <div className="flex items-center gap-4">
                {isAuthenticated && (
                  <NotInterestedButton
                    onClick={handleNotInterested}
                    isDisabled={hideProject.isPending}
                  />
                )}
              </div>
            </div>

            {/* Title */}
            <h1 className="mb-3 font-heading text-xl font-semibold text-primary md:text-display-xs">
              {project.name}
            </h1>

            {/* Description */}
            {project.description && (
              <p className="mb-8 text-sm leading-relaxed text-secondary">
                {project.description}
              </p>
            )}

            {/* Metadata Row */}
            <ProjectMetadataRow
              reward={project.respondentRemunerationFormatted}
              durationMinutes={project.studyLengthMinutes}
              kindOfResearch={project.kindOfResearch}
            />

            {/* Actions Row */}
            {!hasAlreadyApplied && (
              <div className="mt-8 flex flex-wrap items-center gap-4 md:gap-6">
                <ApplyButton
                  isDisabled={
                    (isAuthenticated && !canApply) ||
                    isClosedToNewApplications ||
                    // `isClosedToNewApplications` is waived for invite holders,
                    // so without this a finished study showed the closed banner
                    // beside an enabled Apply (PX-410).
                    isStudyOver
                  }
                  isAuthenticated={isAuthenticated}
                  showSignupToApply={showSignupToApply}
                  isAtScreenerLimit={isAtScreenerLimit}
                  canApply={canApply}
                  isDraft={
                    projectData.screenerResponseStatus ===
                    ScreenerResponseStatus.DRAFT
                  }
                  cycleExpiryDate={
                    projectData.screenerCycle?.cycleExpiryDate ?? undefined
                  }
                  onClick={handleApplyClick}
                  onPlaidVerification={() =>
                    setShowPlaidVerificationModal(true)
                  }
                />
                {projectData.skipScreenerQuestions && (
                  <div className="flex items-center gap-2 text-sm font-medium text-primary">
                    <Trans>No screener questions</Trans>
                    <Tooltip
                      title={
                        <Trans>
                          The researcher invited you directly, so you skip the
                          screener.
                        </Trans>
                      }
                      placement="top"
                    >
                      <TooltipTrigger>
                        <HelpCircle className="size-5 cursor-help text-tertiary" />
                      </TooltipTrigger>
                    </Tooltip>
                  </div>
                )}
                {!projectData.skipScreenerQuestions &&
                  projectData.screenerQuestionsLength !== undefined &&
                  projectData.screenerQuestionsLength > 0 && (
                    <div className="flex items-center gap-2 text-sm font-medium text-primary">
                      <Plural
                        value={projectData.screenerQuestionsLength}
                        one="# screener question"
                        other="# screener questions"
                      />

                      <Tooltip
                        title={
                          <Trans>
                            After you apply, you&apos;ll be asked screener
                            questions to determine whether you qualify for the
                            project.
                          </Trans>
                        }
                        placement="top"
                      >
                        <TooltipTrigger>
                          <HelpCircle className="size-5 cursor-help text-tertiary" />
                        </TooltipTrigger>
                      </Tooltip>
                    </div>
                  )}
              </div>
            )}
          </Card>

          {/* Referral Banner - only for authenticated users with showReferral */}
          {isAuthenticated &&
          projectData.showReferral &&
          projectData.referralLink &&
          projectData.messengerLink ? (
            <>
              <ProjectSpecificReferralsCard
                variant="card"
                className="hidden lg:block"
                backgroundImage={`${BASE_PATH}/images/project-referrals-card-banner.png`}
                referralLink={projectData.referralLink}
                messengerShareLink={projectData.messengerLink}
                projectName={project.name ?? ''}
                incentiveAmount={project.respondentRemuneration ?? 0}
              />

              <ProjectSpecificReferralsCard
                variant="compact"
                className="lg:hidden"
                backgroundImage={`${BASE_PATH}/images/project-referrals-card-banner.png`}
                referralLink={projectData.referralLink}
                messengerShareLink={projectData.messengerLink}
                projectName={project.name ?? ''}
                incentiveAmount={project.respondentRemuneration ?? 0}
              />
            </>
          ) : (
            <ReferralBanner
              referralLink={projectData.referralLink}
              messengerLink={projectData.messengerLink}
            />
          )}
          <WhatToExpect />
        </div>
      </div>
      <FaqSection />

      {search?.cancel === true && user && (
        <CancelApplicationPrompt
          projectId={projectId}
          userId={user.id}
          projectName={project.name ?? ''}
          screenerResponseStatus={projectData.screenerResponseStatus}
          kindOfResearch={project.kindOfResearch}
          taskProgress={taskProgress}
          respondentRemuneration={project.respondentRemuneration}
        />
      )}

      {showWorkEmailVerificationModal && profileData && user && (
        <WorkEmailVerificationModal
          profile={profileData.profile}
          userId={user.id}
          returnPath={globalThis.location.pathname + globalThis.location.search}
          onClose={async () => {
            await Promise.all([
              refetchProfile(),
              queryClient.invalidateQueries({
                queryKey: queryKeys.session.current(),
              }),
              queryClient.refetchQueries({
                queryKey: queryKeys.projects.detailBase(projectId),
              }),
            ]);

            setShowWorkEmailVerificationModal(false);
          }}
        />
      )}
    </>
  );
}
